import React, { useRef, useCallback, useState, useEffect } from 'react';
import { ReactFlow, addEdge, useNodesState, useEdgesState, Controls, Background, useReactFlow } from 'reactflow';
import { useDnD } from '../../context/DnDContext';
import NormalizerNode from '../nodes/NormalizerNode/NormalizerNode'; 
import InputNode from '../nodes/InputNode/InputNode';
import ScalerNode from '../nodes/ScalerNode/ScalerNode';
import CapperNode from '../nodes/CapperNode/CapperNode';
import LossSubtractorNode from '../nodes/LossSubtractorNode/LossSubtractorNode';
import AdderNode from '../nodes/AdderNode/AdderNode';
import MatcherNode from '../nodes/MatcherNode/MatcherNode';
import ConsumptionNode from '../nodes/ConsumptionNode/ConsumptionNode';
import WindGenerationNode from '../nodes/WindGenerationNode/WindGenerationNode';
import SolarGenerationNode from '../nodes/SolarGenerationNode/SolarGenerationNode';
import BankingNode from '../nodes/BankingNode/BankingNode';
import SettleNode from '../nodes/SettleNode/SettleNode';
import ExtrapolationNode from '../nodes/ExtrapolationNode/ExtrapolationNode';
import FinanceNode from '../nodes/FinanceNode/FinanceNode';
import LoanNode from '../nodes/LoanNode/LoanNode';
import DepreciationNode from '../nodes/DepreciationNode/DepreciationNode';
import OperationalCostsNode from '../nodes/OperationalCostsNode/OperationalCostsNode';
import BatteryNode from '../nodes/BatteryNode/BatteryNode';
import Modal from '../Modal/Modal';
import { usePipeline } from '../../context/PipelineContext'; // Import the context hook
import { updateNodePosition, getPipelineByName, deleteNodeFromPipeline } from '../../api/api'; // Import API functions
import { toast, Bounce } from 'react-toastify';
import 'reactflow/dist/style.css';

// Debounce function to limit how often a function can be called
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const initialNodes = [];

const nodeTypes = {
  input: InputNode,
  normalizer: NormalizerNode,
  scaler: ScalerNode,
  capper: CapperNode,
  loss: LossSubtractorNode,
  adder: AdderNode,
  matcher: MatcherNode,
  consumption: ConsumptionNode,
  windgeneration: WindGenerationNode,
  solargeneration: SolarGenerationNode,
  extrapolation: ExtrapolationNode,
  finance: FinanceNode,
  banking: BankingNode,
  settle: SettleNode,
  loan: LoanNode,
  depreciation: DepreciationNode,
  operationalcosts: OperationalCostsNode,
  battery: BatteryNode,
};

// Create a context for sharing the node ID to name mapping
export const NodeMappingContext = React.createContext({
  updateNodeMapping: () => {},
  getNodeMapping: () => ({})
});

const Canvas = () => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { screenToFlowPosition } = useReactFlow();
  const [type] = useDnD();
  const [isModalOpen, setModalOpen] = useState(false);
  const [nodeName, setNodeName] = useState('');
  const [interval, setInterval] = useState('15 mins');
  const [nodeType, setNodeType] = useState('solar');
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  const [savingNodePosition, setSavingNodePosition] = useState(false);
  const [lastSavedPositions, setLastSavedPositions] = useState({});
  const [nodeIdToNameMap, setNodeIdToNameMap] = useState({});
  const { 
    session, 
    dirtyNodeInputs,
    updateSelectedNode, 
    addInputNode, 
    updateNodePosition: updateContextNodePosition, 
    setNodeConfigured,
    batchAddNodes,
    removeNode,
    markNodeDirty
  } = usePipeline(); // Destructure the functions from context
  
  // Create a debounced version of the updateNodePosition function
  // This will only call the API after the node has stopped moving for 800ms
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateNodePosition = useCallback(
    debounce((pipelineName, nodeId, x, y) => {
      console.log(`Debounced update for node ${nodeId} at position (${x}, ${y})`);
      
      // Skip if pipeline name is not valid
      if (!pipelineName || pipelineName === "Unsaved_Pipeline") {
        console.log("Skipping position update for invalid pipeline");
        return;
      }
      
      // Set saving state to true to show visual indicator
      setSavingNodePosition(true);
      
      // Get the actual node name from the mapping, or use the nodeId if not found
      const actualNodeName = nodeIdToNameMap[nodeId] || nodeId;
      console.log(`Using node name: ${actualNodeName} for node ID: ${nodeId}`);
      
      // Save the position to the backend using the actual node name
      updateNodePosition(pipelineName, actualNodeName, x, y)
        .then(response => {
          console.log('Position saved to backend:', response);
          
          // Show a toast notification
          toast.success(`Node position updated`, {
            position: "bottom-right",
            autoClose: 1000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          
          // Update the last saved position
          setLastSavedPositions(prev => ({
            ...prev,
            [nodeId]: { x, y }
          }));
          
          // Set saving state back to false
          setSavingNodePosition(false);
        })
        .catch(error => {
          console.error('Failed to save position to backend:', error);
          setSavingNodePosition(false);
          
          // Show error toast
          toast.error(`Failed to save position: ${error.message}`, {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
            transition: Bounce,
          });
        });
    }, 800), // Increased debounce time to 800ms to reduce API calls
    [nodeIdToNameMap]
  );
  
  // Log node positions whenever they change
  useEffect(() => {
    console.log('Current node positions:', nodes.map(node => ({
      id: node.id,
      position: node.position
    })));
  }, [nodes]);
  
  // Handle node deletion
  const onNodesDelete = useCallback((nodesToDelete) => {
    if (!session.pipeline_name || session.pipeline_name === "Unsaved_Pipeline") {
      toast.error("Cannot delete nodes from an unsaved pipeline", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    
    // Find all edges connected to the nodes being deleted
    const affectedEdges = edges.filter(edge => 
      nodesToDelete.some(node => node.id === edge.source || node.id === edge.target)
    );
    
    // Track nodes that need to be marked as unconfigured
    const nodesToReconfigure = new Set();
    
    // Identify target nodes that will need reconfiguration
    affectedEdges.forEach(edge => {
      // If the node being deleted is a source node, the target node needs reconfiguration
      if (nodesToDelete.some(node => node.id === edge.source)) {
        nodesToReconfigure.add(edge.target);
      }
    });
    
    console.log('Nodes that will need reconfiguration:', Array.from(nodesToReconfigure));
    
    // Process each node to be deleted
    nodesToDelete.forEach(async (node) => {
      try {
        // Get the actual node name from the mapping
        const actualNodeName = nodeIdToNameMap[node.id] || node.id;
        console.log(`Deleting node: ${actualNodeName}`);
        
        // Call the API to delete the node from the backend
        const response = await deleteNodeFromPipeline(session.pipeline_name, actualNodeName);
        
        // Remove the node from the context state
        removeNode(actualNodeName);
        
        // Remove the node from the nodeIdToNameMap
        const updatedMapping = { ...nodeIdToNameMap };
        delete updatedMapping[node.id];
        setNodeIdToNameMap(updatedMapping);
        
        // Process affected nodes from the API response
        if (response.affected_nodes && response.affected_nodes.length > 0) {
          response.affected_nodes.forEach(affectedNodeName => {
            // Find the node ID from the name
            const affectedNodeId = Object.keys(nodeIdToNameMap).find(
              id => nodeIdToNameMap[id] === affectedNodeName
            );
            
            if (affectedNodeId) {
              console.log(`Marking node ${affectedNodeName} (ID: ${affectedNodeId}) as unconfigured due to source node deletion`);
              
              // Mark the node as unconfigured in the context
              setNodeConfigured(affectedNodeName, false);
              
              // Update the node's data in the ReactFlow state
              setNodes(nds => nds.map(n => {
                if (n.id === affectedNodeId) {
                  // Check node type
                  const isAdderNode = n.type === 'adder';
                  const isMatcherNode = n.type === 'matcher';
                  const isOperationalCostsNode = n.type === 'operationalcosts';
                  const needsReconfigurationFlags = isAdderNode || isMatcherNode || isOperationalCostsNode;
                  
                  return {
                    ...n,
                    data: {
                      ...n.data,
                      isConfigured: false,
                      isApiConfigured: false,
                      // For adder and matcher nodes, we need to set these flags to trigger the visual changes
                      ...(needsReconfigurationFlags && {
                        needsReconfiguration: true,
                        needsPartialReconfiguration: true
                      })
                    }
                  };
                }
                return n;
              }));
            }
          });
        }
        
        // Also handle any nodes identified locally that need reconfiguration
        nodesToReconfigure.forEach(targetNodeId => {
          const targetNodeName = nodeIdToNameMap[targetNodeId] || targetNodeId;
          
          // Check if this node was already handled from the API response
          if (!response.affected_nodes || !response.affected_nodes.includes(targetNodeName)) {
            console.log(`Marking node ${targetNodeName} as unconfigured due to source node deletion (local detection)`);
            
            // Mark the node as unconfigured in the context
            setNodeConfigured(targetNodeName, false);
            
            // Update the node's data in the ReactFlow state
            setNodes(nds => nds.map(n => {
              if (n.id === targetNodeId) {
                // Check node type
                const isAdderNode = n.type === 'adder';
                const isMatcherNode = n.type === 'matcher';
                const isOperationalCostsNode = n.type === 'operationalcosts';
                const needsReconfigurationFlags = isAdderNode || isMatcherNode || isOperationalCostsNode;
                
                return {
                  ...n,
                  data: {
                    ...n.data,
                    isConfigured: false,
                    isApiConfigured: false,
                    // For adder and matcher nodes, we need to set these flags to trigger the visual changes
                    ...(needsReconfigurationFlags && {
                      needsReconfiguration: true,
                      needsPartialReconfiguration: true
                    })
                  }
                };
              }
              return n;
            }));
          }
        });
        
      } catch (error) {
        console.error(`Failed to delete node ${node.id}:`, error);
        toast.error(`Failed to delete node: ${error.message}`, {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        });
      }
    });
    
    // Remove the affected edges from the ReactFlow state
    setEdges(eds => eds.filter(edge => 
      !affectedEdges.some(affectedEdge => 
        affectedEdge.id === edge.id
      )
    ));
    
  }, [session.pipeline_name, nodeIdToNameMap, removeNode, edges, setEdges, setNodes, setNodeConfigured]);
  
  // Convert pipeline nodes to ReactFlow nodes
  const convertPipelineNodesToReactFlowNodes = (pipelineData) => {
    if (!pipelineData || !pipelineData.pipeline || !pipelineData.pipeline.nodes) {
      return [];
    }

    const catalog = pipelineData.catalog || {};
    
    return pipelineData.pipeline.nodes.map(node => {
      // Determine node type based on the function
      const nodeType = node.func === "input_upload" ? "input" : 
                      node.func === "normalizer_func" ? "normalizer" : 
                      node.func === "scaler_func" ? "scaler" : 
                      node.func === "caper_func" ? "capper" : 
                      node.func === "loss_func" ? "loss" : 
                      node.func === "merger_func" ? "adder" : 
                      node.func === "matcher_func" ? "matcher" : 
                      node.func === "consumption_input" ? "consumption" : 
                      node.func === "wind_generation_node" ? "windgeneration" : 
                      node.func === "solar_generation_node" ? "solargeneration" : 
                      node.func === "banking_func" ? "banking" : 
                      node.func === "settle_node" ? "settle" :
                      node.func === "extrapolation_func" ? "extrapolation" :
                      node.func === "finance_func" ? "finance" :
                      node.func === "loan_amortization_schedule" ? "loan" :
                      node.func === "depreciation_and_Tax_benefits" ? "depreciation" :
                      node.func === "project_operational_cost_func" ? "operationalcosts" :
                      node.func === "battery_func" ? "battery" :
                      "default";
      
      // Extract node parameters from catalog
      const nodeName = node.name;
      let nodeParams = {};
      
      if (nodeType === "input") {
        // For input nodes, get type and interval from catalog
        const type = catalog[`params:type_${nodeName}`] || "solar";
        const interval = catalog[`params:interval_${nodeName}`] || 15;
        nodeParams = { type, interval };
      } else if (nodeType === "normalizer") {
        // For normalizer nodes, get target from catalog
        const target = catalog[`params:target_${nodeName}`] || 15;
        // Convert target to interval format for the UI (15 or 60 mins)
        const interval = target === '15' || target === 15 ? '15 mins' : '60 mins';
        nodeParams = { 
          target,
          interval,
          name: nodeName,
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "scaler") {
        // For scaler nodes, get mult_factor and col_name from catalog
        const multipleFactor = catalog[`params:mult_factor_${nodeName}`] ?? 2;
        
        // Check both possible keys for column name (params:col_name_* and params:mult_col_*)
        // This handles both old and new pipeline formats
        const colNameRaw = catalog[`params:mult_col_${nodeName}`] || catalog[`params:col_name_${nodeName}`] || '';
        const colName = (colNameRaw && colNameRaw !== 'Column name not specified') ? colNameRaw : '';
        
        // Log the catalog entries for debugging
        console.log(`Loading scaler node ${nodeName} from pipeline:`, {
          multipleFactor,
          colName,
          // Log both possible column name keys
          colNameRawOld: catalog[`params:col_name_${nodeName}`],
          colNameRawNew: catalog[`params:mult_col_${nodeName}`],
          // Check if either key exists
          colNameOldExists: `params:col_name_${nodeName}` in catalog,
          colNameNewExists: `params:mult_col_${nodeName}` in catalog,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          multipleFactor,
          colName,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the ScalerNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "capper") {
        // For capper nodes, get acdc_ratio and col_name from catalog
        const acdcRatio = catalog[`params:acdc_ratio_${nodeName}`];
        const KA = catalog[`params:KA_${nodeName}`];
        const MH = catalog[`params:MH_${nodeName}`];
        const genToTransLoss = catalog[`params:gen_to_trans_loss_${nodeName}`];
        const capLimit = catalog[`params:cap_limit_${nodeName}`];
        
        // Check both possible keys for column name (params:col_name_* and params:cap_col_*)
        // This handles both old and new pipeline formats
        const capColRaw = catalog[`params:cap_col_${nodeName}`] || catalog[`params:col_name_${nodeName}`] || '';
        const colName = (capColRaw && capColRaw !== 'Column name not specified') ? capColRaw : '';
        
        // Log the catalog entries for debugging
        console.log(`Loading capper node ${nodeName} from pipeline:`, {
          acdcRatio,
          colName,
          KA,
          MH,
          genToTransLoss,
          // Log both possible column name keys
          colNameRawOld: catalog[`params:col_name_${nodeName}`],
          colNameRawNew: catalog[`params:cap_col_${nodeName}`],
          // Check if either key exists
          colNameOldExists: `params:col_name_${nodeName}` in catalog,
          colNameNewExists: `params:cap_col_${nodeName}` in catalog,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          acdcRatio,
          colName,
          KA,
          MH,
          genToTransLoss,
          capLimit,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the CapperNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "loss") {
        // For loss subtractor nodes, get transmission and wheeling loss percents (fallback to legacy loss_perc)
        const transmissionLossPerc = catalog[`params:transmission_loss_perc_${nodeName}`];
        const wheelingLossPerc = catalog[`params:wheeling_loss_perc_${nodeName}`];
        const legacyLossPerc = catalog[`params:loss_perc_${nodeName}`];
        const computedLossPerc = (parseFloat(transmissionLossPerc ?? 0) + parseFloat(wheelingLossPerc ?? 0)) || legacyLossPerc || 10;

        // Get column name from the catalog
        const lossColRaw = catalog[`params:loss_col_${nodeName}`] || catalog[`params:col_name_${nodeName}`] || '';
        const colName = (lossColRaw && lossColRaw !== 'Column name not specified') ? lossColRaw : '';
        
        // Log the catalog entries for debugging
        console.log(`Loading loss subtractor node ${nodeName} from pipeline:`, {
          transmissionLossPerc,
          wheelingLossPerc,
          legacyLossPerc,
          computedLossPerc,
          colName,
          // Log column name key
          colNameRaw: catalog[`params:loss_col_${nodeName}`],
          // Check if key exists
          colNameExists: `params:loss_col_${nodeName}` in catalog,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          transmissionLossPerc,
          wheelingLossPerc,
          lossPerc: computedLossPerc,
          colName,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the LossSubtractorNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "adder") {
        // For adder nodes, get inputA_col_name, inputB_col_name, and sum_column_name from catalog
        const inputA_raw = catalog[`params:inputA_col_${nodeName}`] || '';
        const inputB_raw = catalog[`params:inputB_col_${nodeName}`] || '';
        const sum_raw = catalog[`params:sum_column_name_${nodeName}`] || '';
        const inputA_col_name = (inputA_raw && inputA_raw !== 'Column A not specified') ? inputA_raw : '';
        const inputB_col_name = (inputB_raw && inputB_raw !== 'Column B not specified') ? inputB_raw : '';
        const sum_column_name = (sum_raw && sum_raw !== 'Sum column not specified') ? sum_raw : '';
        
        // Log the catalog entries for debugging
        console.log(`Loading adder node ${nodeName} from pipeline:`, {
          inputA_col_name,
          inputB_col_name,
          sum_column_name,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          inputA_col_name,
          inputB_col_name,
          sum_column_name,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the AdderNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "matcher") {
        // For matcher nodes, get cons_col_name and gen_col_name from catalog
        // Check all possible parameter naming patterns
        const cons_raw = catalog[`params:inputA_col_${nodeName}`] ||
                         catalog[`params:cons_col_name_${nodeName}`] ||
                         catalog[`params:cons_col_${nodeName}`] ||
                         '';
        const cons_col_name = (cons_raw && cons_raw !== "Consumption column not specified") ? cons_raw : '';
                             
        const gen_raw = catalog[`params:inputB_col_${nodeName}`] ||
                        catalog[`params:gen_col_name_${nodeName}`] ||
                        catalog[`params:gen_col_${nodeName}`] ||
                        '';
        const gen_col_name = (gen_raw && gen_raw !== "Generation column not specified") ? gen_raw : '';
        
        // Log the catalog entries for debugging
        console.log(`Loading matcher node ${nodeName} from pipeline:`, {
          cons_col_name,
          gen_col_name,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          cons_col_name,
          gen_col_name,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the MatcherNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "consumption") {
        // For consumption nodes, get the file name from catalog
        const fileRaw = catalog[`params:file_name_${nodeName}`] || '';
        const fileName = (fileRaw && fileRaw !== "File not specified") ? fileRaw : '';
        const inputMode = catalog[`params:consumption_input_mode_${nodeName}`] || 'hourly_csv';
        const year = catalog[`params:consumption_year_${nodeName}`] || 2025;
        const preview = catalog[`params:consumption_preview_${nodeName}`] || null;
        const bankingPolicyName = catalog[`params:consumption_banking_policy_${nodeName}`] || preview?.bankingPolicyName || '';
        const existingOaReUnits = Number(catalog[`params:existing_oa_re_units_${nodeName}`] || 0);
        const existingBtmUnits = Number(catalog[`params:existing_btm_units_${nodeName}`] || 0);
        const existingOaType = catalog[`params:existing_oa_type_${nodeName}`] || 'solar';
        const existingOaInputMode = catalog[`params:existing_oa_input_mode_${nodeName}`] || 'annual';
        const existingOaHourlyFile = catalog[`params:existing_oa_hourly_file_${nodeName}`] || '';
        const existingOaFileName = String(existingOaHourlyFile).split('/').pop() || '';
        let units = [];
        let balanceMode = 'calculated';
        let slotMonthlyData = '';
        if (inputMode === 'slot_monthly') {
          slotMonthlyData = catalog[`params:slot_monthly_data_${nodeName}`] || '';
        }
        if (inputMode === 'multi_unit_slot_monthly') {
          try {
            const rawPayload = catalog[`params:slot_monthly_data_${nodeName}`];
            const parsed = typeof rawPayload === 'string' ? JSON.parse(rawPayload || '{}') : (rawPayload || {});
            units = Array.isArray(parsed?.units) ? parsed.units : [];
            balanceMode = ['manual', 'user_input', 'no_permissible_limit'].includes(String(parsed?.balance_mode || parsed?.balanceMode || '').toLowerCase())
              ? 'manual'
              : 'calculated';
          } catch (error) {
            console.warn(`Could not parse multi-unit payload for ${nodeName}`, error);
          }
        }
        
        console.log(`Loading consumption node ${nodeName} from pipeline:`, {
          fileName,
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          fileName,
          inputMode,
          year,
          preview,
          bankingPolicyName,
          existingOaReUnits,
          existingBtmUnits,
          existingOaType,
          existingOaInputMode,
          existingOaFileName,
          balanceMode,
          units,
          slotMonthlyData,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the ConsumptionNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "windgeneration") {
        // For wind generation nodes, get the interval and file_path from catalog
        const file_path = catalog[`params:file_path_${nodeName}`] || `./gen_upload/${nodeName}.csv`;
        const mwPerTurbine =
          catalog[`params:mw_per_turbine_${nodeName}`]
          ?? catalog?.['params:GLOBAL_PARAMS']?.mw_per_turbine
          ?? 2.1;
        console.log(`Loading wind generation node ${nodeName} from pipeline:`, {
          file_path,
          mwPerTurbine,
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          file_path,
          mw_per_turbine: mwPerTurbine,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the WindGenerationNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "solargeneration") {
        // For solar generation nodes, get the interval, file_path and BTM flag from catalog
        const file_path = catalog[`params:file_path_${nodeName}`] || `./gen_upload/${nodeName}.csv`;
        const btmFlagRaw = catalog[`params:Btm_flag_${nodeName}`];
        const btmFlag =
          typeof btmFlagRaw === 'boolean'
            ? btmFlagRaw
            : (btmFlagRaw === 'true' ? true : false);
        
        console.log(`Loading solar generation node ${nodeName} from pipeline:`, {
          file_path,
          btmFlag,
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          file_path,
          btmFlag,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the SolarGenerationNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "banking") {
        // For banking nodes, get the banking_type from catalog
        const bankingType = catalog[`params:banking_type_${nodeName}`] || "daily_tod";
        
        console.log(`Loading banking node ${nodeName} from pipeline:`, {
          bankingType,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          bankingType,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the BankingNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "settle") {
        // For settle nodes, the source label, banking rule, fee and generation CSV live in the catalog
        const sourceLabel = catalog[`params:settle_source_label_${nodeName}`] || nodeName;
        const bankingType = catalog[`params:settle_banking_type_${nodeName}`] || 'yearly_without_tod';
        const bankingFeeRaw = catalog[`params:settle_banking_fee_${nodeName}`];
        const bankingFee = (bankingFeeRaw === undefined || bankingFeeRaw === null || bankingFeeRaw === '') ? 0.1 : Number(bankingFeeRaw);
        const generationFile = catalog[`params:settle_generation_file_${nodeName}`] || '';
        const generationFileName = String(generationFile).split('/').pop().replace(/^[0-9a-f-]{36}_/i, '');
        const inputNodeName = (Array.isArray(node.inputs) && node.inputs.length > 0)
          ? node.inputs[node.inputs.length - 1]
          : null;

        nodeParams = {
          sourceLabel,
          bankingType,
          bankingFee,
          generationFileName,
          inputNodeName,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the SettleNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "extrapolation") {
        // For extrapolation nodes, get all the parameters from catalog
        const yearsRaw = catalog[`params:years_${nodeName}`];
        const bankableYearsRaw = catalog[`params:bankable_years_${nodeName}`];
        const windDepPercRaw = catalog[`params:wind_dep_perc_${nodeName}`];
        const solarDepPercRaw = catalog[`params:solar_dep_perc_${nodeName}`];
        const bankingChargeRaw = catalog[`params:banking_charge_${nodeName}`];
        const consAppPercRaw = catalog[`params:cons_app_perc_${nodeName}`];
        const years = (yearsRaw === undefined || yearsRaw === null || yearsRaw === '') ? 30 : yearsRaw;
        const bankableYears = (bankableYearsRaw === undefined || bankableYearsRaw === null || bankableYearsRaw === '') ? 25 : bankableYearsRaw;
        const windDepPerc = (windDepPercRaw === undefined || windDepPercRaw === null || windDepPercRaw === '') ? 0 : windDepPercRaw;
        const solarDepPerc = (solarDepPercRaw === undefined || solarDepPercRaw === null || solarDepPercRaw === '') ? 0.5 : solarDepPercRaw;
        const bankingCharge = (bankingChargeRaw === undefined || bankingChargeRaw === null || bankingChargeRaw === '') ? 8 : bankingChargeRaw;
        const consAppPerc = (consAppPercRaw === undefined || consAppPercRaw === null || consAppPercRaw === '') ? 0 : consAppPercRaw;
        const totWindGenCol = catalog[`params:tot_wind_gen_col_${nodeName}`] || '';
        const totSolarGenCol = catalog[`params:tot_solar_gen_col_${nodeName}`] || '';
        const tConsCol = catalog[`params:t_cons_col_${nodeName}`] || '';
        
        console.log(`Loading extrapolation node ${nodeName} from pipeline:`, {
          years,
          bankableYears,
          windDepPerc,
          solarDepPerc,
          bankingCharge,
          totWindGenCol,
          totSolarGenCol,
          consAppPerc,
          tConsCol,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          years,
          bankableYears,
          windDepPerc,
          solarDepPerc,
          bankingCharge,
          totWindGenCol,
          totSolarGenCol,
          consAppPerc,
          tConsCol,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the ExtrapolationNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      }else if (nodeType === "finance") {
        // For finance node - compute tariff from node-level inputs (standard or GH)
        const mode = String(catalog[`params:discom_tariff_calc_mode_${nodeName}`] ?? 'standard').toLowerCase();
        const x = parseFloat(catalog[`params:units_X_${nodeName}`] ?? 0) || 0;
        const A = parseFloat(catalog[`params:energy_cost_A_${nodeName}`] ?? 0) || 0;
        const B = parseFloat(catalog[`params:wheeling_charge_B_${nodeName}`] ?? 0) || 0;
        const C = parseFloat(catalog[`params:electricity_duty_C_${nodeName}`] ?? 0) || 0;
        const D = parseFloat(catalog[`params:tod_charges_D_${nodeName}`] ?? 0) || 0;
        const E = parseFloat(catalog[`params:tax_E_${nodeName}`] ?? 0) || 0;
        const F = parseFloat(catalog[`params:fac_F_${nodeName}`] ?? 0) || 0;
        const G = parseFloat(catalog[`params:rebate_G_${nodeName}`] ?? 0) || 0;
        const subsidy = Math.abs(parseFloat(catalog[`params:universal_subsidy_${nodeName}`] ?? 0) || 0);
        const gh = (suffix) => parseFloat(catalog[`params:gh_${suffix}_${nodeName}`] ?? 0) || 0;
        const subsidyPerUnit = x ? subsidy / x : 0;
        const ghPerUnit = (
          gh('energy_charge') +
          gh('fuel_surcharge') +
          gh('peak_surcharge') +
          (-Math.abs(gh('solar_peak_discount'))) +
          (-Math.abs(gh('pf_incentive'))) +
          (-Math.abs(gh('ehv_rebate'))) +
          gh('demand_charges') +
          gh('electricity_duty')
          - subsidyPerUnit
        );
        const perUnitDiscomCost = mode === 'gh' ? ghPerUnit : (x ? (A + B + C + D + E + F - G - subsidy) / x : 0);

        console.log(`Loading finance node ${nodeName} from pipeline:`, {
          mode, x: x, A, B, C, D, E, F, G, computed: perUnitDiscomCost,
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });

        nodeParams = {
          perUnitDiscomCost,
          name: nodeName,
          isConfigured: true,
          isApiConfigured: true
        };
      } else if (nodeType === "loan") {
        // For loan nodes, get loan parameters from catalog
        const loanPerc = catalog[`params:loan_perc_${nodeName}`] || 80;
        const annualRate = catalog[`params:annual_rate_${nodeName}`] || 9;
        const tenureYears = catalog[`params:tenure_years_${nodeName}`] || 9;
        const moratoriumYears = catalog[`params:moratorium_years_${nodeName}`] || 0;
        
        console.log(`Loading loan node ${nodeName} from pipeline:`, {
          loanPerc,
          annualRate,
          tenureYears,
          moratoriumYears,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          loanPerc,
          annualRate,
          tenureYears,
          moratoriumYears,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the LoanNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "depreciation") {
        // For depreciation nodes, get depreciation parameters from catalog
        const BasicPriceWind = catalog[`params:Basic_price_wind_${nodeName}`];
        const BasicPriceSolar = catalog[`params:Basic_price_solar_${nodeName}`];
        const BasicPriceSolarBtm = catalog[`params:Basic_price_solar_btm_${nodeName}`] ?? 0;
        const depRateForITWDV = catalog[`params:dep_rate_for_IT_WDV_${nodeName}`];
        const addnDepRate = catalog[`params:addn_dep_rate_${nodeName}`];
        const taxRate = catalog[`params:tax_rate_${nodeName}`] || 23;
        const gstInput = catalog[`params:gst_input_${nodeName}`];
        const gstPerc = catalog[`params:gst_perc_${nodeName}`];
        
        console.log(`Loading depreciation node ${nodeName} from pipeline:`, {
          BasicPriceWind,
          BasicPriceSolar,
          BasicPriceSolarBtm,
          depRateForITWDV,
          addnDepRate,
          taxRate,
          gstInput,
          gstPerc,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          BasicPriceWind,
          BasicPriceSolar,
          BasicPriceSolarBtm,
          depRateForITWDV,
          addnDepRate,
          taxRate,
          gstInput: Boolean(gstInput),
          gstPerc,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the DepreciationNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      } else if (nodeType === "battery") {
        const genColName = catalog[`params:gen_col_${nodeName}`] || '';
        const batteryHours = catalog[`params:battery_hours_${nodeName}`] ?? 2;
        const batteryCapacityKw = catalog[`params:battery_capacity_kw_${nodeName}`] ?? '';
        const roundTripEfficiency = catalog[`params:round_trip_efficiency_${nodeName}`] ?? 90;
        const dod = catalog[`params:dod_${nodeName}`] ?? 90;
        const batteryCapexPerKwh = catalog[`params:battery_capex_per_kwh_${nodeName}`] ?? '';
        const batteryOmPerc = catalog[`params:battery_om_perc_${nodeName}`] ?? 2;
        const loanPerc = catalog[`params:loan_perc_${nodeName}`] ?? 80;
        const annualRate = catalog[`params:annual_rate_${nodeName}`] ?? 9;
        const tenureYears = catalog[`params:tenure_years_${nodeName}`] ?? 9;
        const depRateForITWDV = catalog[`params:dep_rate_for_IT_WDV_${nodeName}`] ?? 40;
        const addnDepRate = catalog[`params:addn_dep_rate_${nodeName}`] ?? 0;
        const taxRate = catalog[`params:tax_rate_${nodeName}`] ?? 25;
        const gstInput = Boolean(catalog[`params:gst_input_${nodeName}`]);
        const gstPerc = catalog[`params:gst_perc_${nodeName}`] ?? 0;
        const batteryAssumptions = catalog[`params:battery_assumptions_${nodeName}`] || { enabled: false };
        const chargeTimeSlots = catalog[`params:charge_time_slots_${nodeName}`] || [];
        const dischargeTimeSlots = catalog[`params:discharge_time_slots_${nodeName}`] || [];
        const sourceInput = Array.isArray(node.inputs) && typeof node.inputs[0] === 'string'
          ? node.inputs[0].replace(/_output$/, '')
          : '';

        nodeParams = {
          genColName,
          batteryHours,
          batteryCapacityKw,
          roundTripEfficiency,
          dod,
          batteryCapexPerKwh,
          batteryOmPerc,
          loanPerc,
          annualRate,
          tenureYears,
          depRateForITWDV,
          addnDepRate,
          taxRate,
          gstInput,
          gstPerc,
          batteryAssumptions,
          chargeTimeSlots,
          dischargeTimeSlots,
          inputNodeName: sourceInput,
          name: nodeName,
          isConfigured: true,
          isApiConfigured: true
        };
      } else if (nodeType === "operationalcosts") {
        // For operational costs nodes, get all parameters from catalog
        const noOfBopYear = catalog[`params:no_of_bop_year_${nodeName}`] || 0;
        // Legacy escalation_perc maps to wind_escalation_perc; solar defaults to 0 if missing
        const windBopEscalationPerc = catalog[`params:wind_bop_escalation_perc_${nodeName}`] ?? catalog[`params:escalation_perc_${nodeName}`] ?? 0;
        const windEscalationPerc = catalog[`params:wind_escalation_perc_${nodeName}`] ?? 0;
        const solarEscalationPerc = catalog[`params:solar_escalation_perc_${nodeName}`] ?? 0;
        const discountRate = catalog[`params:discount_rate_${nodeName}`] || 0;
        const annualMaintenancePerTurbineInLakhs = catalog[`params:annual_maintenance_per_turbine_in_lakhs_${nodeName}`] || 0;
        const annualMaintenancePerTurbineInLakhsWithBop = catalog[`params:annual_maintenance_per_turbine_in_lakhs_with_bop_${nodeName}`] || 0;
        const annualMaintenancePerSolarUnitInLakhs = catalog[`params:annual_maintenance_per_solar_unit_in_lakhs_${nodeName}`] || 0;
        const insurancePercentOfAssetCost = catalog[`params:insurance_percent_of_asset_cost_${nodeName}`] || 0;
        const operationalCharges = catalog[`params:operational_charges_${nodeName}`] || 0;
        const assetManagementFees = catalog[`params:asset_management_fees_${nodeName}`] || 0;
        const annualLeaseChargePerAcreSolar = catalog[`params:annual_lease_charge_per_acre_solar_${nodeName}`] ?? catalog[`params:annual_lease_charge_per_acre_${nodeName}`] ?? 0;
        const annualLeaseChargePerAcreWind = catalog[`params:annual_lease_charge_per_acre_wind_${nodeName}`] ?? catalog[`params:annual_lease_charge_per_acre_${nodeName}`] ?? 0;
        const annualLeaseEscalationPerc = catalog[`params:annual_lease_escalation_perc_${nodeName}`] || 0;
        const solarAcresPerMWp = catalog[`params:solar_acres_per_MWp_${nodeName}`] || 0;
        const windAcresPerWTG = catalog[`params:wind_acres_per_WTG_${nodeName}`] || 0;
        const firstYearBenifits = catalog[`params:first_year_benifits_${nodeName}`] || true;
        const revenueFromExcessBankedUnitsPerUnit = catalog[`params:revenue_from_excess_banked_units_per_unit_${nodeName}`] || 0;
        const totalLapsedUnitsForExcessRevenuePerc = catalog[`params:total_lapsed_units_for_excess_revenue_perc_${nodeName}`] || 0;
        const transmissionChargesPerKWh = catalog[`params:transmission_charges_per_KWh_${nodeName}`] || 0;
        const wheelingCharges = catalog[`params:wheeling_charges_${nodeName}`] || 0;
        const electricityDutyPerUnit = catalog[`params:electricity_duty_per_unit_${nodeName}`] || 0;
        const bankingChargeRs = catalog[`params:banking_charge_rs_${nodeName}`] ?? 0;
        const perKW = catalog[`params:per_KW_${nodeName}`] || false;
        const perKWh = catalog[`params:per_KWh_${nodeName}`] || false;
        const transmissionChargesPerKwPerMonth = catalog[`params:transmission_charges_per_Kw_per_month_${nodeName}`] || 0;
        console.log(`Loading operational costs node ${nodeName} from pipeline:`, {
          noOfBopYear,
          windBopEscalationPerc,
          windEscalationPerc,
          solarEscalationPerc,
          annualMaintenancePerTurbineInLakhs,
          annualMaintenancePerTurbineInLakhsWithBop,
          annualMaintenancePerSolarUnitInLakhs,
          insurancePercentOfAssetCost,
          operationalCharges,
          assetManagementFees,
          annualLeaseChargePerAcreSolar,
          annualLeaseChargePerAcreWind,
          annualLeaseEscalationPerc,
          solarAcresPerMWp,
          windAcresPerWTG,
          firstYearBenifits,
          revenueFromExcessBankedUnitsPerUnit,
          totalLapsedUnitsForExcessRevenuePerc,
          transmissionChargesPerKWh,
          wheelingCharges,
          electricityDutyPerUnit,
          bankingChargeRs,
          discountRate,
          // Log all catalog entries for this node
          catalogEntries: Object.keys(catalog)
            .filter(key => key.includes(nodeName))
            .reduce((obj, key) => {
              obj[key] = catalog[key];
              return obj;
            }, {})
        });
        
        nodeParams = { 
          no_of_bop_year: noOfBopYear,
          wind_bop_escalation_perc: windBopEscalationPerc,
          wind_escalation_perc: windEscalationPerc,
          solar_escalation_perc: solarEscalationPerc,
          discount_rate: discountRate,
          annual_maintenance_per_turbine_in_lakhs: annualMaintenancePerTurbineInLakhs,
          annual_maintenance_per_turbine_in_lakhs_with_bop: annualMaintenancePerTurbineInLakhsWithBop,
          annual_maintenance_per_solar_unit_in_lakhs: annualMaintenancePerSolarUnitInLakhs,
          insurance_percent_of_asset_cost: insurancePercentOfAssetCost,
          operational_charges: operationalCharges,
          asset_management_fees: assetManagementFees,
          annual_lease_charge_per_acre_solar: annualLeaseChargePerAcreSolar,
          annual_lease_charge_per_acre_wind: annualLeaseChargePerAcreWind,
          annual_lease_escalation_perc: annualLeaseEscalationPerc,
          solar_acres_per_MWp: solarAcresPerMWp,
          wind_acres_per_WTG: windAcresPerWTG,
          first_year_benifits: firstYearBenifits,
          revenue_from_excess_banked_units_per_unit: revenueFromExcessBankedUnitsPerUnit,
          total_lapsed_units_for_excess_revenue_perc: totalLapsedUnitsForExcessRevenuePerc,
          transmission_charges_per_KWh: transmissionChargesPerKWh,
          wheeling_charges: wheelingCharges,
          electricity_duty_per_unit: electricityDutyPerUnit,
          banking_charge_rs: bankingChargeRs,
          per_KW: perKW,
          per_KWh: perKWh,
          transmission_charges_per_Kw_per_month: transmissionChargesPerKwPerMonth,
          name: nodeName,
          isConfigured: true,  // Explicitly mark as configured for the OperationalCostsNode component
          isApiConfigured: true  // Mark as configured since it's from a saved pipeline
        };
      }
      
      // Create ReactFlow node
      return {
        id: nodeName,
        type: nodeType,
        position: node.position || { x: 0, y: 0 },
        data: { 
          label: `${nodeType} node: ${nodeName}`,
          ...nodeParams
        }
      };
    });
  };
  
  // Create edges (connections) between nodes based on pipeline data
  const createEdgesFromPipelineNodes = (pipelineData) => {
    if (!pipelineData || !pipelineData.pipeline || !pipelineData.pipeline.nodes) {
      return [];
    }
    
    const edges = [];
    const nodes = pipelineData.pipeline.nodes;
    
    // Map of output names to source node IDs
    const outputToNodeMap = {};
    nodes.forEach(node => {
      if (node.outputs) {
        const outputs = Array.isArray(node.outputs) ? node.outputs : [node.outputs];
        outputs.forEach((output) => {
          outputToNodeMap[output] = node.name;
        });
      }
    });
    
    // Create edges based on inputs that reference other nodes' outputs
    nodes.forEach(node => {
      // Special handling for matcher nodes which have two inputs (consumption and generation)
      if (node.func === "matcher_func" && node.inputs && Array.isArray(node.inputs)) {
        // For matcher nodes, the first two inputs are the node outputs
        // cons_node_name + "_output" and gen_node_name + "_output"
        const consInput = node.inputs[0]; // First input (cons_node_name + "_output")
        const genInput = node.inputs[1]; // Second input (gen_node_name + "_output")
        
        // Get the source node IDs for both inputs
        const consSourceNodeId = outputToNodeMap[consInput];
        const genSourceNodeId = outputToNodeMap[genInput];
        
        console.log(`Creating edges for matcher node ${node.name}:`, {
          consInput,
          genInput,
          consSourceNodeId,
          genSourceNodeId
        });
        
        // Create edge for consumption input
        if (consSourceNodeId) {
          edges.push({
            id: `${consSourceNodeId}-${node.name}-cons`,
            source: consSourceNodeId,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'cons_input', // Consumption input handle for matcher node
          });
        }
        
        // Create edge for generation input
        if (genSourceNodeId) {
          edges.push({
            id: `${genSourceNodeId}-${node.name}-gen`,
            source: genSourceNodeId,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'gen_input', // Generation input handle for matcher node
          });
        }
      }
      // Special handling for merger/adder nodes which have two inputs
      else if (node.func === "merger_func" && node.inputs && Array.isArray(node.inputs)) {
        // For merger/adder nodes, the first two inputs are the node outputs
        // input_node_1_name + "_output" and input_node_2_name + "_output"
        const input1 = node.inputs[0]; // First input (input_node_1_name + "_output")
        const input2 = node.inputs[1]; // Second input (input_node_2_name + "_output")
        
        // Get the source node IDs for both inputs
        const sourceNodeId1 = outputToNodeMap[input1];
        const sourceNodeId2 = outputToNodeMap[input2];
        
        console.log(`Creating edges for adder node ${node.name}:`, {
          input1,
          input2,
          sourceNodeId1,
          sourceNodeId2
        });
        
        // Create edge for first input
        if (sourceNodeId1) {
          edges.push({
            id: `${sourceNodeId1}-${node.name}-1`,
            source: sourceNodeId1,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'input1', // First input handle for adder node
          });
        }
        
        // Create edge for second input
        if (sourceNodeId2) {
          edges.push({
            id: `${sourceNodeId2}-${node.name}-2`,
            source: sourceNodeId2,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'input2', // Second input handle for adder node
          });
        }
      }
      // Special handling for extrapolation nodes which have two inputs from the same source node
      else if (node.func === "extrapolation_func" && node.inputs && Array.isArray(node.inputs)) {
        // For extrapolation nodes, the input node name is directly stored as the last parameter
        // The extrapolation_func signature is:
        // extrapolation_func(df_with_banking, df_without_banking, years, bankable_years, yoy_dep_perc, node_name, input_node_name)
        
        // Get the input node name directly from the inputs array (it's the last parameter)
        const inputNodeName = node.inputs[node.inputs.length - 1];
        
        console.log(`Creating edge for extrapolation node ${node.name}:`, {
          inputNodeName,
          allInputs: node.inputs,
          outputToNodeMap
        });
        
        // The source node ID is the input node name itself
        let sourceNodeId = inputNodeName;
        
        // Create a single edge from the input node to the extrapolation node
        if (sourceNodeId) {
          edges.push({
            id: `${sourceNodeId}-${node.name}`,
            source: sourceNodeId,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'input', // Standard input handle for extrapolation node
          });
          
          console.log(`Created edge from ${sourceNodeId} to ${node.name}`);
        } else {
          console.warn(`Could not create edge for extrapolation node ${node.name}: input node not found`);
        }
      }
      // Special handling for operational costs nodes which have three inputs
      else if (node.func === "project_operational_cost_func" && node.inputs && Array.isArray(node.inputs)) {
        // For operational costs nodes, we expect three inputs:
        // [extrapolation_node_output, loan_node_output, depreciation_node_output]
        const extrapolationInput = node.inputs[0]; // First input (extrapolation node output)
        const loanInput = node.inputs[1]; // Second input (loan node output)
        const depTaxInput = node.inputs[2]; // Third input (depreciation/tax node output)
        
        // Get the source node IDs for all three inputs
        const extrapolationSourceNodeId = outputToNodeMap[extrapolationInput];
        const loanSourceNodeId = outputToNodeMap[loanInput];
        const depTaxSourceNodeId = outputToNodeMap[depTaxInput];
        
        console.log(`Creating edges for operational costs node ${node.name}:`, {
          extrapolationInput,
          loanInput,
          depTaxInput,
          extrapolationSourceNodeId,
          loanSourceNodeId,
          depTaxSourceNodeId
        });
        
        // Create edge for extrapolation input
        if (extrapolationSourceNodeId) {
          edges.push({
            id: `${extrapolationSourceNodeId}-${node.name}-extrapolation`,
            source: extrapolationSourceNodeId,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'extrapolation_input', // Extrapolation input handle for operational costs node
          });
        }
        
        // Create edge for loan input
        if (loanSourceNodeId) {
          edges.push({
            id: `${loanSourceNodeId}-${node.name}-loan`,
            source: loanSourceNodeId,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'loan_input', // Loan input handle for operational costs node
          });
        }
        
        // Create edge for depreciation/tax input
        if (depTaxSourceNodeId) {
          edges.push({
            id: `${depTaxSourceNodeId}-${node.name}-dep-tax`,
            source: depTaxSourceNodeId,
            target: node.name,
            sourceHandle: 'output',
            targetHandle: 'depreciation_input', // Depreciation input handle for operational costs node
          });
        }
      } else if (node.inputs && Array.isArray(node.inputs)) {
        // Standard handling for other node types
        node.inputs.forEach(input => {
          // Check if this input references another node's output
          const sourceNodeId = outputToNodeMap[input];
          if (sourceNodeId) {
            edges.push({
              id: `${sourceNodeId}-${node.name}`,
              source: sourceNodeId,
              target: node.name,
              sourceHandle: 'output', // Assuming the output handle ID is 'output'
              targetHandle: 'input',  // Assuming the input handle ID is 'input'
            });
          }
        });
      }
    });
    
    return edges;
  };

  // Track whether we've already loaded the pipeline
  const [pipelineLoaded, setPipelineLoaded] = useState({});

  const resolveNodeName = useCallback((nodeId) => {
    const sourceNode = nodes.find((n) => n.id === nodeId);
    if (sourceNode?.data?.name) return sourceNode.data.name;
    return nodeIdToNameMap[nodeId] || nodeId;
  }, [nodes, nodeIdToNameMap]);

  const getCurrentInputLinksForTarget = useCallback((targetNodeId, edgesSnapshot = edges) => {
    const targetNode = nodes.find((n) => n.id === targetNodeId);
    if (!targetNode) return [];

    const orderByType = {
      adder: ['input1', 'input2'],
      matcher: ['cons_input', 'gen_input'],
      operationalcosts: ['extrapolation_input', 'loan_input', 'depreciation_input']
    };

    const edgesForTarget = edgesSnapshot.filter((e) => e.target === targetNodeId);
    const handleOrder = orderByType[targetNode.type];

    if (handleOrder) {
      const links = [];
      handleOrder.forEach((handleId, index) => {
        const edge = edgesForTarget.find((e) => (e.targetHandle || 'input') === handleId);
        if (!edge) return;
        links.push({
          handle: handleId,
          inputName: resolveNodeName(edge.source),
          inputIndex: index
        });
      });
      return links;
    }

    return edgesForTarget
      .sort((a, b) => String(a.targetHandle || 'input').localeCompare(String(b.targetHandle || 'input')))
      .map((edge, index) => ({
        handle: edge.targetHandle || 'input',
        inputName: resolveNodeName(edge.source),
        inputIndex: index
      }));
  }, [edges, nodes, resolveNodeName]);
  
  // Mark a node as requiring reconfiguration when its upstream changes
  const markNodeAsNeedingReconfiguration = useCallback((targetNodeId, { partial = false, edgesSnapshot = edges } = {}) => {
    const targetName = nodeIdToNameMap[targetNodeId] || targetNodeId;
    const targetNode = nodes.find((n) => n.id === targetNodeId);
    const isCurrentlyConfigured = Boolean(targetNode?.data?.isConfigured || targetNode?.data?.isApiConfigured);
    if (!isCurrentlyConfigured) return;

    const inputLinks = getCurrentInputLinksForTarget(targetNodeId, edgesSnapshot);
    markNodeDirty(targetName, {
      nodeType: targetNode?.type || null,
      inputLinks,
      inputNodeNames: inputLinks.map((link) => link.inputName),
      isPartial: partial
    });

    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== targetNodeId) return n;
        
        const isAdderNode = n.type === 'adder';
        const isMatcherNode = n.type === 'matcher';
        const isOperationalCostsNode = n.type === 'operationalcosts';
        const needsExplicitFlags = isAdderNode || isMatcherNode || isOperationalCostsNode;
        
        return {
          ...n,
          data: {
            ...n.data,
            isApiConfigured: false,
            ...(needsExplicitFlags && { needsReconfiguration: true }),
            ...(needsExplicitFlags && partial && { needsPartialReconfiguration: true })
          }
        };
      })
    );
  }, [edges, nodeIdToNameMap, nodes, getCurrentInputLinksForTarget, markNodeDirty, setNodes]);
  
  // Function to update the node ID to name mapping
  const updateNodeMapping = useCallback((nodeId, nodeName) => {
    console.log(`Updating node mapping: ${nodeId} -> ${nodeName}`);
    setNodeIdToNameMap(prev => ({
      ...prev,
      [nodeId]: nodeName
    }));
  }, []);
  
  // Reset the pipelineLoaded state when the session is reset
  useEffect(() => {
    if (session.pipeline_name === "Unsaved_Pipeline") {
      // Reset the pipelineLoaded state
      setPipelineLoaded({});
    }
  }, [session.pipeline_name]);
  
  // Load pipeline data from the backend when the pipeline name changes
  useEffect(() => {
    const pipelineLoadKey = session.pipeline_id || session.pipeline_name;
    // Only load if we haven't loaded this pipeline before
    if (
      session.pipeline_name && 
      session.pipeline_name !== "Unsaved_Pipeline" && 
      !pipelineLoaded[pipelineLoadKey]
    ) {
      console.log(`Loading pipeline: ${session.pipeline_name}`);
      
      // Mark this pipeline as loaded to prevent infinite loops
      setPipelineLoaded(prev => ({
        ...prev,
        [pipelineLoadKey]: true
      }));
      
      // Fetch the complete pipeline data
      getPipelineByName(session.pipeline_name)
        .then(data => {
          console.log('Loaded pipeline data:', data);
          
          if (data) {
            // First, clear any existing nodes and edges
            setNodes([]);
            setEdges([]);
            
            // Convert pipeline nodes to ReactFlow nodes
            const reactFlowNodes = convertPipelineNodesToReactFlowNodes(data);
            console.log('Converted to ReactFlow nodes:', reactFlowNodes);
            
            // Create edges between nodes
            const reactFlowEdges = createEdgesFromPipelineNodes(data);
            console.log('Created edges:', reactFlowEdges);
            
            // Set the nodes and edges in ReactFlow
            setNodes(reactFlowNodes);
            setEdges(reactFlowEdges);
            
            // Initialize the lastSavedPositions with the positions from the backend
            const initialPositions = {};
            // Also initialize the node ID to name mapping
            const initialNodeMapping = {};
            
            reactFlowNodes.forEach(node => {
              initialPositions[node.id] = node.position;
              // For each node, map its ID to its name (which are the same in this case)
              initialNodeMapping[node.id] = node.id;
              console.log(`Initializing node mapping for loaded node: ${node.id} -> ${node.id}`);
            });
            
            setLastSavedPositions(initialPositions);
            setNodeIdToNameMap(initialNodeMapping);
            
            // Instead of updating the context node by node, we'll create a batch update
            // This prevents the useEffect from being triggered multiple times
            const nodesToAdd = [];
            
            reactFlowNodes.forEach(node => {
              // Extract node data
              const { id: nodeName, data: nodeData, position, type: nodeType } = node;
              
              if (nodeType === 'input') {
                // Collect input node data
                nodesToAdd.push({
                  name: nodeName,
                  type: nodeData.type,
                  interval: nodeData.interval,
                  position: position,
                  isConfigured: true,
                  nodeType: 'input'
                });
              } else if (nodeType === 'normalizer') {
                // Collect normalizer node data
                nodesToAdd.push({
                  name: nodeName,
                  target: nodeData.target,
                  interval: nodeData.interval,
                  position: position,
                  isConfigured: true,
                  nodeType: 'normalizer'
                });
              } else if (nodeType === 'scaler') {
                // Collect scaler node data
                nodesToAdd.push({
                  name: nodeName,
                  multipleFactor: nodeData.multipleFactor,
                  colName: nodeData.colName,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true, // Explicitly mark as API configured
                  nodeType: 'scaler'
                });
              } else if (nodeType === 'capper') {
                // Collect capper node data
                nodesToAdd.push({
                  name: nodeName,
                  acdcRatio: nodeData.acdcRatio,
                  colName: nodeData.colName,
                  KA: nodeData.KA,
                  MH: nodeData.MH,
                  genToTransLoss: nodeData.genToTransLoss,
                  capLimit: nodeData.capLimit,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'capper'
                });
              } else if (nodeType === 'loss') {
                // Collect loss subtractor node data
                nodesToAdd.push({
                  name: nodeName,
                  lossPerc: nodeData.lossPerc,
                  colName: nodeData.colName,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'loss'
                });
              } else if (nodeType === 'adder') {
                // Collect adder node data
                nodesToAdd.push({
                  name: nodeName,
                  inputA_col_name: nodeData.inputA_col_name,
                  inputB_col_name: nodeData.inputB_col_name,
                  sum_column_name: nodeData.sum_column_name,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'adder'
                });
              } else if (nodeType === 'matcher') {
                // Collect matcher node data
                nodesToAdd.push({
                  name: nodeName,
                  cons_col_name: nodeData.cons_col_name,
                  gen_col_name: nodeData.gen_col_name,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'matcher'
                });
              } else if (nodeType === 'consumption') {
                // Collect consumption node data
                // Get the file name from the node data, handling different possible property names
                const fileName = nodeData.fileName || nodeData.file_name;
                console.log(`Adding consumption node ${nodeName} to batch with fileName:`, fileName);
                
                nodesToAdd.push({
                  name: nodeName,
                  fileName: fileName,
                  inputMode: nodeData.inputMode || nodeData.consumption_input_mode,
                  year: nodeData.year || nodeData.consumption_year,
                  preview: nodeData.preview || nodeData.consumption_preview,
                  bankingPolicyName: nodeData.bankingPolicyName || nodeData.consumption_banking_policy,
                  units: nodeData.units || [],
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'consumption'
                });
              } else if (nodeType === 'windgeneration') {
                // Collect wind generation node data
                const interval = nodeData.interval;
                const file_path = nodeData.file_path || `./gen_upload/${nodeName}.csv`;
                const mw_per_turbine = nodeData.mw_per_turbine ?? 2.1;
                console.log(`Adding wind generation node ${nodeName} to batch with interval:`, interval, "and file_path:", file_path, "and mw_per_turbine:", mw_per_turbine);
                
                nodesToAdd.push({
                  name: nodeName,
                  interval: interval,
                  file_path: file_path,
                  mw_per_turbine,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'windgeneration'
                });
              } else if (nodeType === 'solargeneration') {
                // Collect solar generation node data
                const interval = nodeData.interval;
                const file_path = nodeData.file_path || `./gen_upload/${nodeName}.csv`;
                console.log(`Adding solar generation node ${nodeName} to batch with interval:`, interval, "and file_path:", file_path);
                
                nodesToAdd.push({
                  name: nodeName,
                  interval: interval,
                  file_path: file_path,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'solargeneration'
                });
              } else if (nodeType === 'extrapolation') {
                // Collect extrapolation node data with all new parameters
                const years = nodeData.years || 30;
                const bankableYears = nodeData.bankableYears || 25;
                const windDepPerc = nodeData.windDepPerc || 0;
                const solarDepPerc = nodeData.solarDepPerc || 0.5;
                const bankingCharge = nodeData.bankingCharge || 8;
                const totWindGenCol = nodeData.totWindGenCol || 'Scaled_Wind O/P';
                const totSolarGenCol = nodeData.totSolarGenCol || 'Scaled_Solar O/P (P75)';
                const consAppPerc = nodeData.consAppPerc || 0;
                const tConsCol = nodeData.tConsCol || 'Hourly Consumption (kWh)';
                
                console.log(`Adding extrapolation node ${nodeName} to batch with all parameters:`, {
                  years, bankableYears, windDepPerc, solarDepPerc, bankingCharge,
                  totWindGenCol, totSolarGenCol, consAppPerc, tConsCol
                });
                
                nodesToAdd.push({
                  name: nodeName,
                  years: years,
                  bankableYears: bankableYears,
                  windDepPerc: windDepPerc,
                  solarDepPerc: solarDepPerc,
                  bankingCharge: bankingCharge,
                  totWindGenCol: totWindGenCol,
                  totSolarGenCol: totSolarGenCol,
                  consAppPerc: consAppPerc,
                  tConsCol: tConsCol,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'extrapolation'
                });
              }
              else if (nodeType === 'finance') {
                // Collect finance node data - updated to use new parameter names
                const perUnitDiscomCost = nodeData.perUnitDiscomCost || 9.92;
                console.log(`Adding finance node ${nodeName} to batch with perUnitDiscomCost:`, perUnitDiscomCost);
                
                nodesToAdd.push({
                  name: nodeName,
                  perUnitDiscomCost: perUnitDiscomCost,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'finance'
                });
              }
              else if (nodeType === 'loan') {
                // Collect loan node data
                const loanPerc = nodeData.loanPerc || 80;
                const annualRate = nodeData.annualRate || 9;
                const tenureYears = nodeData.tenureYears || 9;
                const moratoriumYears = nodeData.moratoriumYears || 1;
                console.log(`Adding loan node ${nodeName} to batch with parameters:`, {
                  loanPerc, annualRate, tenureYears, moratoriumYears
                });
                
                nodesToAdd.push({
                  name: nodeName,
                  loanPerc: loanPerc,
                  annualRate: annualRate,
                  tenureYears: tenureYears,
                  moratoriumYears: moratoriumYears,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'loan'
                });
              }
              else if (nodeType === 'depreciation') {
                // Collect depreciation node data
                const BasicPriceWind = nodeData.BasicPriceWind || 0;
                const BasicPriceSolar = nodeData.BasicPriceSolar || 0;
                const depRateForITWDV = nodeData.depRateForITWDV || 0;
                const addnDepRate = nodeData.addnDepRate || 0;
                const taxRate = nodeData.taxRate || 0;
                const gstPerc = nodeData.gstPerc;
                console.log(`Adding depreciation node ${nodeName} to batch with parameters:`, {
                  BasicPriceWind, BasicPriceSolar, depRateForITWDV, addnDepRate, taxRate, gstPerc
                });
                
                nodesToAdd.push({
                  name: nodeName,
                  BasicPriceWind,
                  BasicPriceSolar,
                  depRateForITWDV,
                  addnDepRate,
                  taxRate,
                  gstPerc,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'depreciation'
                });
              }
              else if (nodeType === 'battery') {
                nodesToAdd.push({
                  name: nodeName,
                  genColName: nodeData.genColName,
                  batteryHours: nodeData.batteryHours,
                  batteryCapacityKw: nodeData.batteryCapacityKw,
                  roundTripEfficiency: nodeData.roundTripEfficiency,
                  dod: nodeData.dod,
                  batteryCapexPerKwh: nodeData.batteryCapexPerKwh,
                  batteryOmPerc: nodeData.batteryOmPerc,
                  loanPerc: nodeData.loanPerc,
                  annualRate: nodeData.annualRate,
                  tenureYears: nodeData.tenureYears,
                  depRateForITWDV: nodeData.depRateForITWDV,
                  addnDepRate: nodeData.addnDepRate,
                  taxRate: nodeData.taxRate,
                  gstInput: nodeData.gstInput,
                  gstPerc: nodeData.gstPerc,
                  batteryAssumptions: nodeData.batteryAssumptions,
                  chargeTimeSlots: nodeData.chargeTimeSlots,
                  dischargeTimeSlots: nodeData.dischargeTimeSlots,
                  inputNodeName: nodeData.inputNodeName,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'battery'
                });
              }
              else if (nodeType === 'operationalcosts') {
                // Collect operational costs node data
                const noOfBopYear = nodeData.noOfBopYear || 0;
                const escalationPerc = nodeData.escalationPerc || 0;
                const annualMaintenancePerTurbineInLakhs = nodeData.annualMaintenancePerTurbineInLakhs || 0;
                const annualMaintenancePerTurbineInLakhsWithBop = nodeData.annualMaintenancePerTurbineInLakhsWithBop || 0;
                const annualMaintenancePerSolarUnitInLakhs = nodeData.annualMaintenancePerSolarUnitInLakhs || 0;
                const insurancePercentOfAssetCost = nodeData.insurancePercentOfAssetCost || 0;
                const operationalCharges = nodeData.operationalCharges || 0;
                const assetManagementFees = nodeData.assetManagementFees || 0;
                const annualLeaseChargePerAcre = nodeData.annualLeaseChargePerAcre || 0;
                const annualLeaseEscalationPerc = nodeData.annualLeaseEscalationPerc || 0;
                const solarAcresPerMWp = nodeData.solarAcresPerMWp || 0;
                const windAcresPerWTG = nodeData.windAcresPerWTG || 0;
                const firstYearBenifits = nodeData.firstYearBenifits || true;
                const revenueFromExcessBankedUnitsPerUnit = nodeData.revenueFromExcessBankedUnitsPerUnit || 0;
                const totalLapsedUnitsForExcessRevenuePerc = nodeData.totalLapsedUnitsForExcessRevenuePerc || 0;
                const transmissionChargesPerKWh = nodeData.transmissionChargesPerKWh || 0;
                const wheelingCharges = nodeData.wheelingCharges || 0;
                const electricityDutyPerUnit = nodeData.electricityDutyPerUnit || 0;
                const discountRate = nodeData.discountRate || nodeData.discount_rate || 0;
                const bankingChargeRs = nodeData.banking_charge_rs ?? nodeData.bankingChargeRs ?? 0;
                
                console.log(`Adding operational costs node ${nodeName} to batch with parameters:`, {
                  noOfBopYear, escalationPerc, annualMaintenancePerTurbineInLakhs,
                  annualMaintenancePerTurbineInLakhsWithBop, annualMaintenancePerSolarUnitInLakhs,
                  insurancePercentOfAssetCost, operationalCharges,
                  assetManagementFees, annualLeaseChargePerAcre, annualLeaseEscalationPerc, solarAcresPerMWp, windAcresPerWTG,
                  firstYearBenifits, revenueFromExcessBankedUnitsPerUnit, totalLapsedUnitsForExcessRevenuePerc,
                  transmissionChargesPerKWh, wheelingCharges, electricityDutyPerUnit, discountRate
                });
                
                nodesToAdd.push({
                  name: nodeName,
                  noOfBopYear: noOfBopYear,
                  escalationPerc: escalationPerc,
                  annualMaintenancePerTurbineInLakhs: annualMaintenancePerTurbineInLakhs,
                  annualMaintenancePerTurbineInLakhsWithBop: annualMaintenancePerTurbineInLakhsWithBop,
                  annualMaintenancePerSolarUnitInLakhs: annualMaintenancePerSolarUnitInLakhs,
                  insurancePercentOfAssetCost: insurancePercentOfAssetCost,
                  operationalCharges: operationalCharges,
                  assetManagementFees: assetManagementFees,
                  annualLeaseChargePerAcreSolar: nodeData.annual_lease_charge_per_acre_solar ?? nodeData.annualLeaseChargePerAcreSolar ?? 0,
                  annualLeaseChargePerAcreWind: nodeData.annual_lease_charge_per_acre_wind ?? nodeData.annualLeaseChargePerAcreWind ?? 0,
                  annualLeaseEscalationPerc: annualLeaseEscalationPerc,
                  solarAcresPerMWp: solarAcresPerMWp,
                  windAcresPerWTG: windAcresPerWTG,
                  firstYearBenifits: firstYearBenifits,
                  revenueFromExcessBankedUnitsPerUnit: revenueFromExcessBankedUnitsPerUnit,
                  totalLapsedUnitsForExcessRevenuePerc: totalLapsedUnitsForExcessRevenuePerc,
                  transmissionChargesPerKWh: transmissionChargesPerKWh,
                  wheelingCharges: wheelingCharges,
                  electricityDutyPerUnit: electricityDutyPerUnit,
                  bankingChargeRs: bankingChargeRs,
                  discountRate: discountRate,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'operationalcosts'
                });
              }
              else if (nodeType === 'settle') {
                nodesToAdd.push({
                  name: nodeName,
                  sourceLabel: nodeData.sourceLabel,
                  bankingType: nodeData.bankingType,
                  bankingFee: nodeData.bankingFee,
                  generationFileName: nodeData.generationFileName,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'settle'
                });
              }
              else if (nodeType === 'banking') {
                // Collect banking node data
                nodesToAdd.push({
                  name: nodeName,
                  bankingType: nodeData.bankingType,
                  position: position,
                  isConfigured: true,
                  isApiConfigured: true,
                  nodeType: 'banking'
                });
              }
            });
            
            // Now update the context in a single batch operation
            if (nodesToAdd.length > 0) {
              console.log('Adding nodes to context in batch:', nodesToAdd);
              // Use the new batchAddNodes function to update all nodes at once
              batchAddNodes(nodesToAdd);
            }
          }
        })
        .catch(error => {
          console.error('Failed to load pipeline data:', error);
        });
    }
  }, [session.pipeline_name, session.pipeline_id, pipelineLoaded, setNodes, setEdges, batchAddNodes]);

  // Modified onConnect function to enforce one-to-one connections
  const onConnect = useCallback((params) => {
    // Check if pipeline is unsaved
    if (!session.pipeline_name || session.pipeline_name === "Unsaved_Pipeline") {
      toast.error("Cannot connect nodes in an unsaved pipeline. Please create a new pipeline or open an existing one.", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    
    // Check if the source node already has a connection with the same sourceHandle
    const hasExistingConnection = edges.some(
      edge => edge.source === params.source && edge.sourceHandle === params.sourceHandle
    );
    
    // Log connection details for debugging
    console.log('Connection attempt:', {
      params,
      sourceNode: nodes.find(node => node.id === params.source),
      targetNode: nodes.find(node => node.id === params.target),
      nodeMapping: nodeIdToNameMap
    });
    
    // Only add the new edge if there's no existing connection from this source handle
    if (!hasExistingConnection) {
      setEdges((eds) => {
        const nextEdges = addEdge(params, eds);
        markNodeAsNeedingReconfiguration(params.target, { edgesSnapshot: nextEdges });
        return nextEdges;
      });
      
      // Log the successful connection
      console.log('Connection created:', {
        source: params.source,
        sourceName: nodeIdToNameMap[params.source] || params.source,
        target: params.target,
        targetName: nodeIdToNameMap[params.target] || params.target
      });
    } else {
      console.log('Connection rejected: Source node already has a connection');
    }
  }, [edges, setEdges, nodes, nodeIdToNameMap, session.pipeline_name, markNodeAsNeedingReconfiguration]);

  const onEdgesDelete = useCallback((edgesToDelete) => {
    const removedEdgeIds = new Set(edgesToDelete.map((edge) => edge.id));
    const remainingEdges = edges.filter((edge) => !removedEdgeIds.has(edge.id));
    edgesToDelete.forEach((edge) => {
      markNodeAsNeedingReconfiguration(edge.target, { partial: true, edgesSnapshot: remainingEdges });
    });
  }, [edges, markNodeAsNeedingReconfiguration]);

  const onBeforeDelete = useCallback(({ nodes: nodesToDelete = [] }) => {
    if (nodesToDelete.length === 0) return true;
    const nodeNames = nodesToDelete.map((node) => nodeIdToNameMap[node.id] || node.id);
    const message =
      nodesToDelete.length === 1
        ? `Delete node "${nodeNames[0]}"?`
        : `Delete ${nodesToDelete.length} nodes?\n${nodeNames.join(', ')}`;
    return window.confirm(message);
  }, [nodeIdToNameMap]);

  // Keep dirty-node input links in sync with latest canvas edges
  useEffect(() => {
    const dirtyEntries = Object.values(dirtyNodeInputs || {});
    if (dirtyEntries.length === 0) return;

    dirtyEntries.forEach((entry) => {
      const nodeName = entry.nodeName;
      if (!nodeName) return;

      const nodeId =
        Object.keys(nodeIdToNameMap).find((id) => (nodeIdToNameMap[id] || id) === nodeName) ||
        nodeName;
      const inputLinks = getCurrentInputLinksForTarget(nodeId, edges);
      const inputNodeNames = inputLinks.map((link) => link.inputName);

      const prevLinks = Array.isArray(entry.inputLinks) ? entry.inputLinks : [];
      const prevNames = Array.isArray(entry.inputNodeNames) ? entry.inputNodeNames : [];

      const linksChanged = JSON.stringify(prevLinks) !== JSON.stringify(inputLinks);
      const namesChanged = JSON.stringify(prevNames) !== JSON.stringify(inputNodeNames);

      if (linksChanged || namesChanged) {
        markNodeDirty(nodeName, { inputLinks, inputNodeNames });
      }
    });
  }, [dirtyNodeInputs, nodeIdToNameMap, edges, getCurrentInputLinksForTarget, markNodeDirty]);

  // Custom handler for node changes to specifically log position changes
  const handleNodesChange = useCallback((changes) => {
    // First, call the original handler to update ReactFlow's state
    onNodesChange(changes);
    
    // Track which nodes have been processed to avoid duplicate updates
    const processedNodes = new Set();
    
    // Then process the changes to update our context and backend
    changes.forEach(change => {
      // Check if this is a position change and we haven't processed this node yet
      if (change.type === 'position' && change.position && !processedNodes.has(change.id)) {
        const nodeId = change.id;
        const position = change.position;
        
        // Mark this node as processed
        processedNodes.add(nodeId);
        
        // Update the position in the PipelineContext
        updateContextNodePosition(nodeId, position);
        
        // Check if the position has changed significantly from the last saved position
        const lastSaved = lastSavedPositions[nodeId] || { x: 0, y: 0 };
        const hasMoved = Math.abs(lastSaved.x - position.x) > 5 || Math.abs(lastSaved.y - position.y) > 5;
        
        // Use the debounced function to update the position
        // This will automatically handle debouncing and only call the API
        // after the node has stopped moving for a while
        if (hasMoved && session.pipeline_name !== "Unsaved_Pipeline") {
          console.log(`Debouncing position update for node ${nodeId}`);
          debouncedUpdateNodePosition(
            session.pipeline_name,
            nodeId,
            position.x,
            position.y
          );
        }
      }
    });
  }, [onNodesChange, updateContextNodePosition, session.pipeline_name, lastSavedPositions, debouncedUpdateNodePosition]);

  const onDragOver = useCallback((event) => {
    // Check if pipeline is unsaved
    if (!session.pipeline_name || session.pipeline_name === "Unsaved_Pipeline") {
      event.preventDefault();
      return;
    }
    
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, [session.pipeline_name]);

  // No longer needed - removed to fix ESLint warning

  const onDrop = useCallback(
  (event) => {
    event.preventDefault();
    
    // Check if pipeline is unsaved
    if (!session.pipeline_name || session.pipeline_name === "Unsaved_Pipeline") {
      toast.error("Cannot add nodes to an unsaved pipeline. Please create a new pipeline or open an existing one.", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    
    const nodeType = type; // Assuming 'type' is set correctly in the context
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });

    if (nodeType === 'input') {
      // For input nodes, just store the position and open the modal
      // We'll create the actual node after configuration
      setNodePosition(position);
      setModalOpen(true);
    } else {
      // For normalizer nodes, we need to create a more user-friendly ID
      // that will also be used as the node name
      const timestamp = Date.now();
      const nodeId = `n${timestamp % 10000}`; // Create a shorter, more user-friendly ID
      
      // For other node types, create them immediately
      let nodeData = { 
        label: `${nodeType.charAt(0).toUpperCase() + nodeType.slice(1)} Node: ${nodeId}`, 
        interval: null,
        name: nodeId // Store the name in the data
      };
      
      // Add file_path for wind and solar generation nodes
      if (nodeType === 'windgeneration' || nodeType === 'solargeneration') {
        nodeData.file_path = `./gen_upload/${nodeId}.csv`;
      }
      
      const newNode = {
        id: nodeId, // Use the user-friendly ID
        type: nodeType,
        position,
        data: nodeData
      };
      
      // Update the node ID to name mapping
      updateNodeMapping(nodeId, nodeId);
      
      setNodes((nds) => nds.concat(newNode));
    }
  },
  [screenToFlowPosition, type, setNodes, setNodePosition, setModalOpen, updateNodeMapping, session.pipeline_name]
);

  
  const handleNodeConfigured = async (name, type, interval) => {
    // Log the current session state
    console.log("Current session state:", session);
    
    // Convert interval to display format
    const intervalDisplay = interval === 15 ? '15 mins' : '1 hr';
    
    // Create the node in the ReactFlow canvas
    const newNode = {
      id: name,
      type: 'input',
      position: nodePosition,
      data: { label: `${type} node`, type, interval: intervalDisplay }
    };
    
    // Add the node to ReactFlow
    setNodes((nds) => nds.concat(newNode));
    
    // Update the node ID to name mapping
    updateNodeMapping(name, name);
    
    // Add the node to the PipelineContext with position information
    addInputNode(name, type, interval, nodePosition);
    
    // Set the node as configured in the PipelineContext
    setNodeConfigured(name);
    
    // Update the node position in the backend
    try {
      console.log(`Updating node position for ${name} at:`, nodePosition);
      
      // Skip API call if pipeline is unsaved
      if (session.pipeline_name === "Unsaved_Pipeline") {
        console.log("Skipping position update for unsaved pipeline");
        return;
      }
      
      // Make sure we have a valid pipeline name
      if (!session.pipeline_name) {
        console.error("Pipeline name is missing or invalid");
        return;
      }
      
      // Update the node position in the backend
      const response = await updateNodePosition(
        session.pipeline_name,
        name,
        nodePosition.x,
        nodePosition.y
      );
      console.log(`Node ${name} position updated:`, nodePosition,response);
      
      // Update the last saved position
      setLastSavedPositions(prev => ({
        ...prev,
        [name]: nodePosition
      }));
    } catch (error) {
      console.error('Error adding node to pipeline:', error);
    }
  };

  const onNodeClick = useCallback((event, node) => {
    // Check if pipeline is unsaved
    if (!session.pipeline_name || session.pipeline_name === "Unsaved_Pipeline") {
      toast.error("Cannot select nodes in an unsaved pipeline. Please create a new pipeline or open an existing one.", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }
    
    // Get the actual node name from the mapping, or use the node ID if not found
    const actualNodeName = nodeIdToNameMap[node.id] || node.id;
    console.log(`Node clicked: ${node.id}, using actual name: ${actualNodeName}`);
    
    // Update the selected node with the actual node name
    updateSelectedNode(actualNodeName);
  }, [updateSelectedNode, nodeIdToNameMap, session.pipeline_name]);

  // Create the context value
  const nodeMappingContextValue = {
    updateNodeMapping,
    getNodeMapping: () => nodeIdToNameMap
  };

  // Check if the pipeline is "Unsaved_Pipeline"
  const isPipelineUnsaved = !session.pipeline_name || session.pipeline_name === "Unsaved_Pipeline";

  return (
    <NodeMappingContext.Provider value={nodeMappingContextValue}>
      <div className="reactflow-wrapper" ref={reactFlowWrapper} style={{ width: '100%', height: '93vh', position: 'relative' }}>
        {/* Display message when pipeline is unsaved */}
        {isPipelineUnsaved ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            backgroundColor: '#F7F9FB',
            color: '#666',
            fontSize: '1.5rem',
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              padding: '30px',
              maxWidth: '600px'
            }}>
              <h2 style={{ marginBottom: '20px', color: '#333' }}>Canvas Disabled</h2>
              <p>Please create a New Pipeline or Open an existing Pipeline to Continue</p>
            </div>
          </div>
        ) : (
          <>
            {savingNodePosition && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  borderTopColor: 'transparent',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Saving node position...
              </div>
            )}
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange} // Use our custom handler instead of onNodesChange
              onEdgesChange={onEdgesChange}
              onBeforeDelete={onBeforeDelete}
              onEdgesDelete={onEdgesDelete}
              onConnect={onConnect}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onNodeClick={onNodeClick} // Add onNodeClick handler
              onNodesDelete={onNodesDelete} // Add onNodesDelete handler
              isValidConnection={(connection) => {
                // Check if the source node already has a connection with the same sourceHandle
                const hasExistingConnection = edges.some(
                  edge => edge.source === connection.source && edge.sourceHandle === connection.sourceHandle
                );
                
                // Check if the target node already has a connection with the same targetHandle
                const hasExistingTargetConnection = edges.some(
                  edge => edge.target === connection.target && edge.targetHandle === connection.targetHandle
                );
                
                // Only allow the connection if neither the source nor target handles already have connections
                return !hasExistingConnection && !hasExistingTargetConnection;
              }}
              fitView
              nodeTypes={nodeTypes}
              style={{ backgroundColor: '#F7F9FB' }}
            >
              <Controls />
              <Background />
            </ReactFlow>
            <Modal
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              nodeName={nodeName}
              setNodeName={setNodeName}
              interval={interval}
              setInterval={setInterval}
              nodeType={nodeType}
              setNodeType={setNodeType}
              onConfigured={handleNodeConfigured}
            />
          </>
        )}
        <style jsx="true">{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </NodeMappingContext.Provider>
  );
};

export default Canvas;
